/**
 * Inline markers for React components embedded in assistant markdown.
 *
 * Citations and citation buttons are not written by the model as markdown — they
 * are positions in the text that have to become React components. They used to
 * be smuggled through the pipeline as raw HTML strings (`<cite>1</cite>`,
 * `<span index=0>`) which `rehype-raw` re-parsed back into nodes.
 *
 * #2731 removed `rehype-raw`, because it handed message text to parse5, which
 * threw on malformed markup and took the whole widget down (#2730). Without it
 * react-markdown falls back to escaping raw HTML, so those strings started
 * rendering as literal text in every chat app (#2735).
 *
 * Markers are now a private encoding that `remarkComponentMarkers` turns into
 * mdast nodes at parse time. No HTML is parsed, so untrusted message content can
 * never re-enter the pipeline as markup.
 *
 * The delimiters are Unicode Private Use Area code points: they carry no meaning
 * in markdown, so a marker always survives parsing inside a single text node,
 * and they cannot collide with real message content.
 */

const MARKER_OPEN = '\uE000';
const MARKER_CLOSE = '\uE001';

const MARKER_PATTERN = `${MARKER_OPEN}([^${MARKER_OPEN}${MARKER_CLOSE}]*)${MARKER_CLOSE}`;

export type ComponentMarker = { kind: 'cite'; id: string } | { kind: 'citeButtons'; index: number };

export function encodeComponentMarker(marker: ComponentMarker): string {
  return `${MARKER_OPEN}${JSON.stringify(marker)}${MARKER_CLOSE}`;
}

export function stripComponentMarkers(value: string): string {
  return value.replace(new RegExp(MARKER_PATTERN, 'g'), '');
}

/**
 * Minimal structural view of an mdast node. Declared locally rather than
 * imported from `@types/mdast` so this file has no dependency the app does not
 * already declare.
 */
interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: Record<string, unknown>;
}

function decodeComponentMarker(raw: string): ComponentMarker | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }

  const marker = parsed as Partial<ComponentMarker> | null;

  if (marker?.kind === 'cite' && typeof marker.id === 'string') {
    return { kind: 'cite', id: marker.id };
  }
  if (marker?.kind === 'citeButtons' && typeof marker.index === 'number') {
    return { kind: 'citeButtons', index: marker.index };
  }
  return undefined;
}

/**
 * `data.hName` / `hProperties` / `hChildren` are honoured by mdast-util-to-hast,
 * so these nodes reach react-markdown as ordinary `cite` / `span` elements and
 * the existing component overrides in `Messages.tsx` pick them up unchanged.
 */
function markerToNode(marker: ComponentMarker): MdastNode {
  if (marker.kind === 'cite') {
    return {
      type: 'worknetCite',
      data: {
        hName: 'cite',
        hChildren: [{ type: 'text', value: marker.id }],
      },
    };
  }

  return {
    type: 'worknetCiteButtons',
    data: {
      hName: 'span',
      // Read by the `span` override in `Messages.tsx`. This arrived as an HTML
      // attribute before, so it was a string — keep it one.
      hProperties: { index: String(marker.index) },
      hChildren: [],
    },
  };
}

/**
 * Splits a text node's value on markers. Returns `undefined` when the value
 * holds no usable marker, so the caller can leave the original node alone.
 */
function splitOnMarkers(value: string): MdastNode[] | undefined {
  if (!value.includes(MARKER_OPEN)) {
    return undefined;
  }

  const nodes: MdastNode[] = [];
  const regex = new RegExp(MARKER_PATTERN, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(value)) !== null) {
    const marker = decodeComponentMarker(match[1]);
    if (!marker) {
      // Unrecognised marker: leave it in the surrounding text rather than
      // silently swallowing content.
      continue;
    }
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', value: value.slice(lastIndex, match.index) });
    }
    nodes.push(markerToNode(marker));
    lastIndex = match.index + match[0].length;
  }

  if (nodes.length === 0) {
    return undefined;
  }
  if (lastIndex < value.length) {
    nodes.push({ type: 'text', value: value.slice(lastIndex) });
  }
  return nodes;
}

function transformChildren(node: MdastNode): void {
  const children = node.children;
  if (!children?.length) {
    return;
  }

  // Only rebuilt once a child actually splits, so untouched trees keep their
  // original arrays.
  let next: MdastNode[] | undefined;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];

    if (child.type === 'text' && typeof child.value === 'string') {
      const split = splitOnMarkers(child.value);
      if (split) {
        next ??= children.slice(0, i);
        next.push(...split);
        continue;
      }
    } else if (
      (child.type === 'code' || child.type === 'inlineCode') &&
      typeof child.value === 'string'
    ) {
      // Code is rendered verbatim, so a marker there would be shown as garbage.
      child.value = stripComponentMarkers(child.value);
    } else {
      transformChildren(child);
    }

    next?.push(child);
  }

  if (next) {
    node.children = next;
  }
}

export default function remarkComponentMarkers() {
  return (tree: MdastNode): void => {
    transformChildren(tree);
  };
}
